<template>
  <div
    :class="{
      busy: isBusy || isWaitingForValues || loading,
      'document-metada-select': true,
    }"
  >
    <styled-multiselect
      v-model="selectedOption"
      :disabled="disabled || isWaitingForValues || loading"
      :is-error="isError || !isInputValidated"
      :options="possibleValues ?? []"
      @select="handleSelectionChanged"
    >
    </styled-multiselect>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useToast } from '@/composables/useToast';
  import StyledMultiselect from '@/components/StyledMultiselect/StyledMultiselect.vue';
  import {
    ReloadMetadataFunction,
    UpdateMetadataFunction,
    ValidationFunction,
  } from '../DocumentsMetadata/DocumentsMetadata.types';

  interface DocumentMetadataSelectProps {
    disabled?: boolean;
    getMetadataFunction?: ReloadMetadataFunction;
    isError?: boolean;
    isWaitingForValues?: boolean;
    currentValue: string;
    metadataField: string;
    possibleValues?: string[];
    loading?: boolean;
    validationFunction: ValidationFunction;
    updateMetadataFunction: UpdateMetadataFunction;
  }

  const props = withDefaults(defineProps<DocumentMetadataSelectProps>(), {
    disabled: false,
    isError: false,
    isWaitingForValues: false,
  });

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();

  const { createErrorToast } = useToast();

  const isBusy = ref(false);
  const isInputValidated = ref(true);
  const selectedOption = computed(() => props.currentValue);

  function validateInput(): void {
    isInputValidated.value = props.validationFunction(selectedOption.value);
  }

  async function handleSelectionChanged(selection: string): Promise<void> {
    try {
      isBusy.value = true;
      await props.updateMetadataFunction(props.metadataField, selection);
      await props.getMetadataFunction?.();
    } catch {
      createErrorToast();
    } finally {
      validateInput();
      emit('document-data-changed');
      isBusy.value = false;
    }
  }

  watch(
    () => [selectedOption.value, props.validationFunction],
    () => {
      validateInput();
    },
    { immediate: true },
  );
</script>

<style lang="scss" scoped>
  .document-metada-select {
    &.busy {
      .multiselect {
        @include pulsating-animation;
      }
    }
  }
</style>
